import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronLeft,
  ChevronRight,
  Gamepad2,
  Search,
  SearchX,
  Users,
  X,
} from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { Avatar } from "../../components/Avatar/Avatar";
import { Button } from "../../components/Button/Button";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { Skeleton } from "../../components/Skeleton/Skeleton";

import { ApiError, describeError } from "../../lib/errors";
import { formatRelativeShort } from "../../lib/format";
import { getProject } from "../projects/api";
import { listPlayers } from "./api";
import type { PlayerListItem } from "./types";
import styles from "./PlayersListPage.module.css";

const PAGE_SIZE = 25;

// === Formatting helpers ============================================== //

/**
 * Verbose relative time — "6 hours ago", "5 days ago", "2 weeks ago",
 * "3 months ago". Used for the Joined column (mockup state 1).
 */
function formatRelativeLong(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return plural(min, "minute");
  const hr = Math.floor(min / 60);
  if (hr < 24) return plural(hr, "hour");
  const day = Math.floor(hr / 24);
  if (day < 7) return plural(day, "day");
  const week = Math.floor(day / 7);
  if (week < 5) return plural(week, "week");
  const month = Math.floor(day / 30);
  if (month < 12) return plural(month, "month");
  return plural(Math.floor(day / 365), "year");
}

function plural(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? "" : "s"} ago`;
}

/**
 * Build the page-button sequence with ellipsis gaps — mockup state 1 shows
 * `1 2 3 … 500`. Always shows first/last; a window around the current page.
 */
function pageRange(current: number, total: number): (number | "gap")[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const out: (number | "gap")[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) out.push("gap");
  for (let p = start; p <= end; p++) out.push(p);
  if (end < total - 1) out.push("gap");
  out.push(total);
  return out;
}

// === Component ====================================================== //

export function PlayersListPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL is the source of truth for q + page.
  const urlQ = searchParams.get("q") ?? "";
  const urlPage = Math.max(1, Number(searchParams.get("page")) || 1);

  // Local search text — debounced before it drives the query / URL.
  const [q, setQ] = useState(urlQ);
  const [debouncedQ, setDebouncedQ] = useState(urlQ);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Reflect the debounced query + page in the URL. A query change resets to
  // page 1 so paginated results never point at a stale offset.
  useEffect(() => {
    const next = new URLSearchParams();
    if (debouncedQ) next.set("q", debouncedQ);
    const pageForUrl = debouncedQ !== urlQ ? 1 : urlPage;
    if (pageForUrl > 1) next.set("page", String(pageForUrl));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ]);

  const page = debouncedQ !== urlQ ? 1 : urlPage;

  const query = useQuery({
    queryKey: ["players", id, debouncedQ, page],
    queryFn: () =>
      listPlayers(id, { q: debouncedQ, page, limit: PAGE_SIZE }),
    enabled: !!id,
  });

  // Project name for the breadcrumb.
  const { data: project } = useQuery({
    queryKey: ["project", id],
    queryFn: () => getProject(id).then((r) => r.data),
    enabled: !!id,
  });

  const data = query.data;
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasSearch = debouncedQ.length > 0;

  function goToPage(p: number) {
    const next = new URLSearchParams();
    if (debouncedQ) next.set("q", debouncedQ);
    if (p > 1) next.set("page", String(p));
    // Intentionally a history *push* (no `replace`) so Back returns to the
    // previous page — unlike the search-sync effect, which `replace`s so
    // individual keystrokes never pollute the browser history.
    setSearchParams(next);
  }

  function clearSearch() {
    setQ("");
  }

  const header = (
    <PageHeader
      breadcrumb={[
        { label: "All projects", to: "/projects" },
        { label: project?.name ?? "Project" },
        { label: "Players" },
      ]}
      title="Players"
    />
  );

  // --- Search field disabled when there is nothing to search:
  //   • brand-new project — total 0 with no active query (mockup state 3); or
  //   • the initial page load, before any data has arrived (mockup notes:
  //     "Loading: toolbar disabled"). It stays enabled during search-driven
  //     refetches so typing is never interrupted.
  const initialLoading = query.isLoading && !hasSearch && !data;
  const searchDisabled =
    initialLoading ||
    (!query.isLoading && !query.isError && !hasSearch && total === 0);

  const searchField = (
    <div
      className={`${styles.searchInput} ${
        searchDisabled ? styles.searchDisabled : ""
      }`}
    >
      <span className={styles.leading}>
        <Search size={14} aria-hidden="true" />
      </span>
      <input
        placeholder="Search by name, external ID, or UUID…"
        aria-label="Search players"
        value={q}
        disabled={searchDisabled}
        onChange={(e) => setQ(e.target.value)}
      />
      {q.length > 0 && (
        <button
          type="button"
          className={styles.clear}
          aria-label="Clear search"
          onClick={clearSearch}
        >
          <X size={13} aria-hidden="true" />
        </button>
      )}
    </div>
  );

  // === Error state ==================================================== //
  if (query.isError) {
    return (
      <>
        {header}
        <div className={styles.page}>
          <div className={styles.errorCard}>
            <Alert variant="danger" title="Couldn't load players">
              {query.error instanceof ApiError
                ? describeError(query.error.code)
                : describeError("internal")}
            </Alert>
            <div className={styles.errorActions}>
              <Button
                variant="secondary"
                onClick={() => query.refetch()}
              >
                Retry
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // === Render ========================================================= //
  return (
    <>
      {header}
      <div className={styles.page}>
        {/* Toolbar — search + count chip / result line (mockup states 1-2) */}
        <div className={styles.toolbar}>
          {searchField}
          {!hasSearch && (
            <>
              <div className={styles.toolbarSpacer} />
              {!query.isLoading && total > 0 && (
                <span className={styles.countCallout}>
                  <Users size={11} aria-hidden="true" />
                  {total.toLocaleString()} players
                </span>
              )}
            </>
          )}
        </div>

        {/* Search result line (mockup state 2). */}
        {hasSearch && !query.isLoading && total > 0 && (
          <div className={styles.resultLine}>
            <b>{total.toLocaleString()}</b>{" "}
            {total === 1 ? "player matches" : "players match"}{" "}
            <code>{debouncedQ}</code> · substring of the display name ·{" "}
            <button
              type="button"
              className={styles.link}
              onClick={clearSearch}
            >
              Clear search
            </button>
          </div>
        )}

        {/* Table / states. */}
        <div className={styles.tableWrap}>
          {query.isLoading ? (
            <LoadingTable />
          ) : items.length === 0 ? (
            hasSearch ? (
              <div className={styles.emptyState}>
                <div className={styles.ei}>
                  <SearchX size={26} aria-hidden="true" />
                </div>
                <h3>No player matches &quot;{debouncedQ}&quot;</h3>
                <p>
                  Try part of a name, the external ID, or the player UUID.
                </p>
                <button
                  type="button"
                  className={styles.clearLink}
                  onClick={clearSearch}
                >
                  Clear search
                </button>
              </div>
            ) : (
              <div className={styles.emptyState}>
                <div className={styles.ei}>
                  <Gamepad2 size={26} aria-hidden="true" />
                </div>
                <h3>No players yet</h3>
                <p>
                  Players appear here automatically the first time someone
                  signs in to the game from your build. Nothing to set up.
                </p>
              </div>
            )
          ) : (
            <>
              <PlayersTable
                items={items}
                onRowClick={(playerId) =>
                  navigate(`/projects/${id}/players/${playerId}`)
                }
              />
              {/* Pagination footer — hidden on a single page of results. */}
              {totalPages > 1 && (
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  total={total}
                  itemsOnPage={items.length}
                  onGoToPage={goToPage}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

// === Players table ================================================== //

interface PlayersTableProps {
  items: PlayerListItem[];
  onRowClick: (playerId: string) => void;
}

function PlayersTable({ items, onRowClick }: PlayersTableProps) {
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th style={{ width: "42%" }}>Player</th>
          <th className={styles.num}>Score</th>
          <th>Joined</th>
          <th>Updated</th>
          <th style={{ width: 36 }} aria-label="Open player" />
        </tr>
      </thead>
      <tbody>
        {items.map((p) => (
          <tr
            key={p.id}
            role="link"
            tabIndex={0}
            aria-label={`Open ${p.display_name}`}
            onClick={() => onRowClick(p.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onRowClick(p.id);
              }
            }}
          >
            <td>
              <div className={styles.playerCell}>
                <Avatar name={p.display_name} size={32} />
                <span className={styles.info}>
                  <span className={styles.name}>{p.display_name}</span>
                  <span className={styles.eid}>{p.external_id}</span>
                </span>
              </div>
            </td>
            <td className={styles.num}>
              <span className={styles.scoreVal}>
                {p.score.toLocaleString()}
              </span>
            </td>
            <td className={styles.muted}>
              {formatRelativeLong(p.created_at)}
            </td>
            <td className={styles.muted}>
              {formatRelativeShort(p.updated_at)}
            </td>
            <td className={styles.chevCell}>
              <ChevronRight size={14} aria-hidden="true" />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// === Loading skeleton (8 rows, mockup notes) ======================== //

function LoadingTable() {
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th style={{ width: "42%" }}>Player</th>
          <th className={styles.num}>Score</th>
          <th>Joined</th>
          <th>Updated</th>
          <th style={{ width: 36 }} aria-label="Open player" />
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: 8 }).map((_, i) => (
          <tr key={i}>
            <td>
              <div className={styles.skelCell}>
                <Skeleton width={32} height={32} radius={999} />
                <div className={styles.skelLines}>
                  <Skeleton width="40%" height={12} />
                  <Skeleton width="28%" height={10} />
                </div>
              </div>
            </td>
            <td className={styles.num}>
              <Skeleton width={48} height={12} style={{ marginLeft: "auto" }} />
            </td>
            <td>
              <Skeleton width={72} height={12} />
            </td>
            <td>
              <Skeleton width={48} height={12} />
            </td>
            <td>
              <Skeleton width={14} height={14} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// === Pagination footer ============================================== //

interface PaginationProps {
  page: number;
  totalPages: number;
  total: number;
  itemsOnPage: number;
  onGoToPage: (page: number) => void;
}

function Pagination({
  page,
  totalPages,
  total,
  itemsOnPage,
  onGoToPage,
}: PaginationProps) {
  const from = (page - 1) * PAGE_SIZE + 1;
  const to = (page - 1) * PAGE_SIZE + itemsOnPage;
  return (
    <div className={styles.pagination}>
      <span>
        Showing <b>{from.toLocaleString()}</b>–<b>{to.toLocaleString()}</b> of{" "}
        <b>{total.toLocaleString()}</b> players
      </span>
      <div className={styles.pages}>
        <button
          type="button"
          className={styles.pageBtn}
          disabled={page <= 1}
          onClick={() => onGoToPage(page - 1)}
        >
          <ChevronLeft size={12} aria-hidden="true" /> Prev
        </button>
        {pageRange(page, totalPages).map((p, i) =>
          p === "gap" ? (
            <span key={`gap-${i}`} className={styles.pageEllipsis}>
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              className={`${styles.pageBtn} ${
                p === page ? styles.current : ""
              }`}
              aria-current={p === page ? "page" : undefined}
              onClick={() => onGoToPage(p)}
            >
              {p}
            </button>
          ),
        )}
        <button
          type="button"
          className={styles.pageBtn}
          disabled={page >= totalPages}
          onClick={() => onGoToPage(page + 1)}
        >
          Next <ChevronRight size={12} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
